import React, { useState, useEffect } from 'react';
import { FileDown, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { api, API } from './../lib/api';

// Module-level cache so multiple placements share one request per page load
let cvCache = null;
let cvPromise = null;

export const usePublicCv = () => {
  const [cv, setCv] = useState(cvCache);
  useEffect(() => {
    if (cvCache) return;
    if (!cvPromise) {
      cvPromise = api.get('/public-cv').then((r) => {
        cvCache = r.data || { enabled: false };
        return cvCache;
      }).catch(() => {
        cvCache = { enabled: false };
        return cvCache;
      });
    }
    cvPromise.then(setCv);
  }, []);
  return cv;
};

// Tracked download URL (privacy-safe aggregate counters only — no personal data)
export const cvDownloadUrl = (kind, loc) => `${API}/cv/download/${kind}?loc=${encodeURIComponent(loc || 'other')}`;

const PLACEMENT_KEYS = {
  'meet-steve': 'meet_steve',
  experience: 'experience',
  home: 'home',
  platform: 'platform',
  footer: 'footer',
};

// Full section card — light panel with navy primary download button
export const CvDownloadSection = ({ loc = 'other', compact = false }) => {
  const cv = usePublicCv();
  if (!cv || cv.enabled === false) return null;
  const placementKey = PLACEMENT_KEYS[loc];
  if (placementKey && cv.placements && cv.placements[placementKey] === false) return null;
  if (!cv.has_pdf && !cv.has_docx) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid={`cv-download-section-${loc}`}>
      <div className="flex items-start gap-3.5">
        <div className="h-11 w-11 rounded-xl bg-[#1a2744]/5 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 text-[#1a2744]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-serif font-bold text-[#1a2744] text-lg" data-testid="cv-download-title">{cv.title}</h3>
          {!compact && <p className="text-sm text-slate-600 mt-1 leading-relaxed" data-testid="cv-download-description">{cv.description}</p>}
          <div className="mt-3.5 flex flex-col sm:flex-row flex-wrap gap-2.5">
            {cv.has_pdf && (
              <Button asChild className="h-11 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press justify-center" data-testid="cv-download-pdf-btn">
                <a href={cvDownloadUrl('pdf', loc)} download="Steve-Lesperance-CV-2026.pdf">
                  <FileDown className="h-4 w-4 mr-1.5" /> Download CV PDF
                </a>
              </Button>
            )}
            {cv.has_docx && (
              <Button asChild variant="outline" className="h-11 px-5 border-[#1a2744]/40 text-[#1a2744] font-bold btn-press justify-center" data-testid="cv-download-docx-btn">
                <a href={cvDownloadUrl('docx', loc)} download="Steve-Lesperance-CV-2026.docx">
                  <FileDown className="h-4 w-4 mr-1.5" /> Download CV DOCX
                </a>
              </Button>
            )}
          </div>
          {cv.last_updated && <p className="text-[11px] text-slate-400 mt-2.5" data-testid="cv-download-updated">CV updated {cv.last_updated}</p>}
        </div>
      </div>
    </div>
  );
};

// Small footer link variant
export const CvFooterLink = () => {
  const cv = usePublicCv();
  if (!cv || cv.enabled === false || (cv.placements && cv.placements.footer === false)) return null;
  const kind = cv.has_pdf ? 'pdf' : cv.has_docx ? 'docx' : null;
  if (!kind) return null;
  return (
    <a
      href={cvDownloadUrl(kind, 'footer')}
      download={`Steve-Lesperance-CV-2026.${kind}`}
      className="inline-flex items-center gap-1.5 text-sm font-bold text-[#C9A84C] hover:text-[#e8c96a] py-1.5"
      data-testid="footer-cv-download-link"
    >
      <FileDown className="h-4 w-4" /> {cv.button_label}
    </a>
  );
};
